/* @flow */

import classNames from 'classnames';
import { compose, isEmpty } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { getUserAvatar, isUserOnline } from '@e180/domain/lib/user/util';

import styles from './UserAvatar.style';

import DefaultUserAvatar from './DefaultUserAvatar';
import UserOnlineBadge, {
  large as badgeLarge,
} from 'src/shared/components/domain/user/badge/UserOnlineBadge';
import Avatar from 'src/shared/ui/component/image/Avatar';
import DefaultAvatar from 'src/shared/ui/component/image/DefaultAvatar';
import {
  xsmall,
  small,
  medium,
  large,
  xlarge,
  xxlarge,
} from 'src/shared/ui/component/image/setting/avatarVariants';

import type { AvatarProps } from 'src/shared/ui/component/image/type/avatarTypes';
import type { User } from '@e180/domain/lib/user/type';

type Props = AvatarProps & {
  user: User,
  showBadge?: boolean,
  useDefaultAvatar?: boolean,
  onDarkBackground?: boolean,
};

const useStyles = createUseStyles(styles);

const UserAvatar = ({
  layoutClass,
  variant,
  alt,
  user,
  showBadge,
  onDarkBackground,
  useDefaultAvatar,
}: Props): React.Node => {
  const avatar = getUserAvatar(user);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const badgeClasses = classNames(classes.badge, {
    [classes['badge-small']]: variant === small,
    [classes['badge-xsmall']]: variant === xsmall,
    [classes['badge-medium']]: variant === medium,
    [classes['badge-large']]: variant === large,
    [classes['badge-xlarge']]: variant === xlarge,
    [classes['badge-xxlarge']]: variant === xxlarge,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const avatarProps = {
    layoutClass,
    variant,
    alt,
  };

  let avatarElt = <DefaultUserAvatar {...avatarProps} user={user} />;

  if (avatar && !isEmpty(avatar)) {
    avatarElt = <Avatar {...avatarProps} src={avatar} />;
  } else if (useDefaultAvatar) {
    avatarElt = <DefaultAvatar {...avatarProps} />;
  }

  const badgeElt = showBadge && isUserOnline(user) && (
    <UserOnlineBadge
      layoutClass={badgeClasses}
      variant={[large, xlarge, xxlarge].includes(variant) && badgeLarge}
      onDarkBackground={onDarkBackground}
    />
  );

  return (
    <div className={classes.root}>
      {avatarElt}
      {badgeElt}
    </div>
  );
};

// Make variants available
export * from 'src/shared/ui/component/image/setting/avatarVariants';

export default compose(React.memo)(UserAvatar);
